<template>
  <div class="timesheet-dashboard-tab timesheet-tab-shell">
    <div class="dashboard-toolbar timesheet-tab-toolbar">
      <div class="toolbar-top timesheet-toolbar-top">
        <div>
          <h2 class="dashboard-title timesheet-tab-title">Live Dashboard</h2>
          <p class="dashboard-subtitle timesheet-tab-subtitle">
            Real-time attendance and approvals
          </p>
        </div>
        <div class="toolbar-actions timesheet-toolbar-actions">
          <span v-if="polling" class="live-pill">
            <span class="live-dot" />
            Live
          </span>
          <span v-if="lastUpdated" class="updated-label">
            Updated {{ lastUpdated }}
          </span>
          <b-button variant="outline-secondary" size="sm" @click="refresh">
            Refresh
          </b-button>
        </div>
      </div>

      <div class="toolbar-row timesheet-toolbar-panel">
        <div v-if="payPeriodAvailable" class="timeframe-toggle">
          <b-form-radio-group
            v-model="timeframe"
            :options="timeframeOptions"
            buttons
            button-variant="outline-secondary"
            size="sm"
            @change="onTimeframeChange"
          />
        </div>
        <div v-if="timeframe === 'day'" class="date-nav">
          <button type="button" class="nav-btn" @click="changeDate(-1)">
            <b-icon-chevron-left />
          </button>
          <span class="date-label">{{ dateLabel }}</span>
          <button
            type="button"
            class="nav-btn"
            :disabled="isToday"
            @click="changeDate(1)"
          >
            <b-icon-chevron-right />
          </button>
          <button
            v-if="!isToday"
            type="button"
            class="nav-btn nav-btn-text"
            @click="goToToday"
          >
            Today
          </button>
        </div>
        <div v-else class="date-nav">
          <button
            type="button"
            class="nav-btn"
            :disabled="periodOffset <= -52"
            @click="changePeriod(-1)"
          >
            <b-icon-chevron-left />
          </button>
          <span class="date-label">{{ periodLabel }}</span>
          <button
            type="button"
            class="nav-btn"
            :disabled="periodOffset === 0"
            @click="changePeriod(1)"
          >
            <b-icon-chevron-right />
          </button>
          <button
            v-if="periodOffset !== 0"
            type="button"
            class="nav-btn nav-btn-text"
            @click="goToCurrentPeriod"
          >
            Current
          </button>
        </div>
      </div>

      <div class="filters-row dashboard-filters-row">
        <div class="filter-field">
          <label class="filter-label">Status</label>
          <b-form-select
            v-model="filterParams.status"
            :options="statusOptions"
            size="sm"
            @change="refresh"
          />
        </div>
        <div class="filter-field">
          <label class="filter-label">Project</label>
          <b-form-select
            v-model="filterParams.project_id"
            :options="projectOptions"
            size="sm"
            @change="refresh"
          />
        </div>
      </div>
    </div>

    <div class="dashboard-content timesheet-tab-content">
      <DashboardAttendancePanel
        :events="dashboardEvents"
        :can-approve="canApprove"
        :events-title="eventsTitle"
        @approve="onApprove"
        @reject="openRejectModal"
        @reject-with-reason="rejectWithReason"
      />

      <NotClockedInPanel :project-id="filterParams.project_id" />

      <PayPeriodSettingsCard
        v-if="canManageSettings && payPeriodAvailable"
        @pay-period-saved="onPayPeriodSaved"
      />

      <TimezoneSettingsCard
        v-if="canManageSettings"
        @timezone-saved="onTimezoneSaved"
      />

      <LeaveDefaultsSettingsCard
        v-if="canManageSettings && payPeriodAvailable"
      />

      <WeeklyTimesheetSettingsCard v-if="canManageSettings" />

      <DashboardAlertScheduler v-if="canApprove && !isProjectScoped" />
    </div>

    <b-modal
      id="dashboard-reject-modal"
      modal-class="timesheet-modal"
      title="Reject Timesheet"
      ok-title="Confirm"
      @ok="submitReject"
      @hidden="rejectForm = { eventId: null, adminComment: '' }"
    >
      <b-form-group label="Rejection reason (optional)">
        <b-form-input
          v-model="rejectForm.adminComment"
          placeholder="e.g. Times don't match site records"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'
import DashboardAttendancePanel from './DashboardAttendancePanel.vue'
import NotClockedInPanel from './NotClockedInPanel.vue'
import DashboardAlertScheduler from './DashboardAlertScheduler.vue'
import PayPeriodSettingsCard from './PayPeriodSettingsCard.vue'
import TimezoneSettingsCard from './TimezoneSettingsCard.vue'
import LeaveDefaultsSettingsCard from './LeaveDefaultsSettingsCard.vue'
import WeeklyTimesheetSettingsCard from './WeeklyTimesheetSettingsCard.vue'

export default {
  name: 'TimesheetDashboardTab',
  components: {
    DashboardAttendancePanel,
    NotClockedInPanel,
    DashboardAlertScheduler,
    PayPeriodSettingsCard,
    TimezoneSettingsCard,
    LeaveDefaultsSettingsCard,
    WeeklyTimesheetSettingsCard,
  },
  props: {
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      pollInterval: null,
      lastUpdated: null,
      polling: false,
      dashboardEvents: [],
      timeframe: 'day',
      timeframeOptions: [
        { value: 'day', text: 'Day' },
        { value: 'pay_period', text: 'Pay Period' },
      ],
      periodOffset: 0,
      periodMeta: null,
      filterParams: {
        date: dayjs().format('YYYY-MM-DD'),
        status: null,
        project_id: null,
      },
      statusOptions: [
        { value: null, text: 'All Statuses' },
        { value: 'clocked_in', text: 'Clocked In' },
        { value: 'clocked_out', text: 'Clocked Out' },
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Rejected' },
      ],
      rejectForm: { eventId: null, adminComment: '' },
    }
  },
  computed: {
    ...mapGetters('auth', ['isProjectScoped', 'supervisorProjectIds']),
    canApprove() {
      return this.$store.getters['auth/canAccessDashboard']
    },
    canManageSettings() {
      return this.$store.getters['auth/hasPermission'](
        'timesheets.settings.manage',
      )
    },
    timesheetSettings() {
      return this.$store.getters['timesheet/timesheetSettings']
    },
    // Only truthy once /timesheet-settings has loaded successfully. While
    // loading (null) or on an old API (false) all pay-period UI stays hidden
    // and no timeframe params are sent — an old API would ignore them and
    // return unfiltered events.
    payPeriodAvailable() {
      return !!this.timesheetSettings
    },
    periodRangeLabel() {
      var p = this.periodMeta || this.periodFromSettings(this.periodOffset)
      if (!p) return ''
      return (
        dayjs(p.start_date).format('D MMM') +
        ' – ' +
        dayjs(p.end_date).format('D MMM')
      )
    },
    periodLabel() {
      var range = this.periodRangeLabel
      if (!range) return 'Pay Period'
      if (this.periodOffset === 0) return 'Current · ' + range
      return range
    },
    projectOptions() {
      return this.$store.getters['timesheet/projectOptions']
    },
    isToday() {
      return this.filterParams.date === dayjs().format('YYYY-MM-DD')
    },
    dateLabel() {
      var d = dayjs(this.filterParams.date)
      if (this.isToday) return 'Today · ' + d.format('D MMM YYYY')
      return d.format('ddd, D MMM YYYY')
    },
    eventsTitle() {
      if (this.payPeriodAvailable && this.timeframe === 'pay_period') {
        return this.periodRangeLabel
          ? 'Events · ' + this.periodRangeLabel
          : 'Pay Period Events'
      }
      if (this.isToday) return "Today's Events"
      return 'Events · ' + dayjs(this.filterParams.date).format('D MMM YYYY')
    },
  },
  watch: {
    active(isActive) {
      if (isActive) {
        this.refresh()
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
    // Default the project filter to the first available project once options
    // load. Keyed off the freshly-fetched projectOptions (not the possibly-stale
    // auth assigned_project_ids), and immediate so it fires on mount and again
    // when the async fetch resolves. Admins keep their "All Projects" default.
    projectOptions: {
      immediate: true,
      handler(options) {
        if (this.filterParams.project_id != null) return
        if (options.some((o) => o.value == null && !o.disabled)) {
          // "All Projects" (BA) or "All (my projects)" (multi-project
          // supervisor) is available and already the default selection. For
          // a scoped user, created()'s initial refresh() ran before projects
          // had loaded and skipped the fetch (no "All" option existed yet at
          // that point) — re-fire it now that one does. A BA's initial
          // refresh() already fetched immediately, so skip it here.
          if (this.isProjectScoped) this.refresh()
          return
        }
        const first = options.find((o) => o.value != null && !o.disabled)
        if (first) {
          this.filterParams.project_id = first.value
          this.refresh()
        }
      },
    },
  },
  created() {
    this.$store
      .dispatch('project/getProjects', { limit: 100, is_archived: false })
      .catch(function (e) {
        // Don't swallow silently — a failed load now shows an error hint in the
        // project dropdown (see timesheet/projectOptions) and is logged here.
        console.warn('Could not load projects for timesheet filter', e)
      })
    this.$store.dispatch('timesheet/getTimesheetSettings')
    // Default project selection is handled reactively by the projectOptions
    // watcher (fires once the /projects fetch resolves).
    this.refresh()
    this.startPolling()
    if (!this.isProjectScoped) {
      this.$store.dispatch('timesheet/getAlertSchedules').catch(function (e) {
        console.warn('Could not load alert schedules', e)
      })
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    startPolling() {
      if (this.pollInterval) return
      this.pollInterval = setInterval(() => {
        if (this.active) this.refresh()
      }, 30000)
      this.polling = true
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
      this.polling = false
    },
    changeDate(dir) {
      this.filterParams.date = dayjs(this.filterParams.date)
        .add(dir, 'day')
        .format('YYYY-MM-DD')
      this.refresh()
    },
    goToToday() {
      this.filterParams.date = dayjs().format('YYYY-MM-DD')
      this.refresh()
    },
    periodFromSettings(offset) {
      var s = this.timesheetSettings
      if (!s || !Array.isArray(s.periods)) return null
      return (
        s.periods.find(function (p) {
          return p.offset === offset
        }) || null
      )
    },
    changePeriod(dir) {
      var next = this.periodOffset + dir
      if (next > 0 || next < -52) return
      this.periodOffset = next
      // The settings payload only pre-computes labels for a handful of
      // recent periods — beyond that, keep the previous label (rather than
      // blanking it) until refresh() resolves the real range from the live
      // API response, which works for any offset back to -52.
      var cached = this.periodFromSettings(next)
      if (cached) this.periodMeta = cached
      this.refresh()
    },
    goToCurrentPeriod() {
      this.periodOffset = 0
      this.periodMeta = this.periodFromSettings(0)
      this.refresh()
    },
    // Fired by PayPeriodSettingsCard after a successful save/reset. projectId
    // is null for a company-wide change. Re-resolve the pay-period window
    // straight away instead of waiting for a full page reload.
    onPayPeriodSaved(projectId) {
      if (!this.payPeriodAvailable) return
      var viewing = this.filterParams.project_id
      // A change to some other single project doesn't touch this view.
      if (projectId != null && projectId !== viewing) return
      // Company-wide change — the store's settings (and its pre-computed
      // periods) are already refreshed, so the label can update optimistically.
      if (projectId == null) {
        this.periodMeta = this.periodFromSettings(this.periodOffset)
      }
      this.refresh()
    },
    onTimezoneSaved() {
      this.refresh()
    },
    onTimeframeChange() {
      this.refresh()
    },
    formatTime(val) {
      if (!val) return ''
      var d = val instanceof Date ? val : new Date(val)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    async refresh() {
      // A scoped user with a null project_id is either still waiting on the
      // projectOptions watcher to pick their first project (no "All" option
      // available yet) or has deliberately picked "All (my projects)" — only
      // the former should skip the fetch.
      if (this.isProjectScoped && !this.filterParams.project_id) {
        var hasAllOption = this.projectOptions.some(function (o) {
          return o.value == null && !o.disabled
        })
        if (!hasAllOption) return
      }
      try {
        var params = {
          status: this.filterParams.status,
          project_id: this.filterParams.project_id,
        }
        if (this.payPeriodAvailable && this.timeframe === 'pay_period') {
          params.timeframe = 'pay_period'
          params.period_offset = this.periodOffset
        } else if (this.payPeriodAvailable) {
          // Day mode against the new API: let the server resolve the
          // Perth-local calendar-day boundaries.
          params.timeframe = 'day'
          params.date = this.filterParams.date
        } else {
          // Settings not loaded (yet) or old API: legacy behaviour.
          params.date = this.filterParams.date
        }
        this.dashboardEvents =
          (await this.$store.dispatch('timesheet/getEvents', params)) || []
        var meta = this.$store.state.timesheet.eventsMeta
        if (
          this.timeframe === 'pay_period' &&
          meta &&
          meta.period &&
          meta.period.offset === this.periodOffset
        ) {
          this.periodMeta = meta.period
        }
        this.lastUpdated = this.formatTime(new Date())
      } catch (e) {
        this.$bvToast.toast('Failed to load timesheet events.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    // A pending_confirmation entry (a pre-entered "scheduled" shift whose
    // date has passed) must have its confirmation cleared before the API
    // will approve or reject it — see eventStatusLabel() in
    // timesheetHelpers.js and docs/designs/future-date-timesheet-entries.md.
    // Called before the approve/reject dispatch so it stays one click; a
    // failure here aborts the decision (caught by the caller's toast).
    async confirmIfPending(eventId) {
      var item = this.dashboardEvents.find(function (e) {
        return e.id === eventId
      })
      if (item && item.confirmation_status === 'pending_confirmation') {
        await this.$store.dispatch('timesheet/confirmEvent', eventId)
      }
    },
    async onApprove(eventId) {
      try {
        await this.confirmIfPending(eventId)
        await this.$store.dispatch('timesheet/approveEvent', {
          eventId,
          decision: 'approved',
        })
        this.$bvToast.toast('Timesheet approved.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.refresh()
      } catch (e) {
        this.$bvToast.toast('Failed to approve timesheet.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    openRejectModal(eventId) {
      this.rejectForm.eventId = eventId
      this.rejectForm.adminComment = ''
      this.$bvModal.show('dashboard-reject-modal')
    },
    async rejectWithReason(payload) {
      try {
        await this.confirmIfPending(payload.eventId)
        await this.$store.dispatch('timesheet/approveEvent', {
          eventId: payload.eventId,
          decision: 'rejected',
          adminComment: payload.adminComment || undefined,
        })
        this.$bvToast.toast('Timesheet rejected.', {
          title: 'Success',
          variant: 'warning',
          solid: true,
        })
        this.refresh()
      } catch (e) {
        this.$bvToast.toast('Failed to reject timesheet.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async submitReject(bvModalEvent) {
      bvModalEvent.preventDefault()
      try {
        await this.confirmIfPending(this.rejectForm.eventId)
        await this.$store.dispatch('timesheet/approveEvent', {
          eventId: this.rejectForm.eventId,
          decision: 'rejected',
          adminComment: this.rejectForm.adminComment || undefined,
        })
        this.$bvModal.hide('dashboard-reject-modal')
        this.$bvToast.toast('Timesheet rejected.', {
          title: 'Success',
          variant: 'warning',
          solid: true,
        })
        this.refresh()
      } catch (e) {
        this.$bvToast.toast('Failed to reject timesheet.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.timesheet-dashboard-tab {
  background: #f8fafc;
}

.live-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #166534;
  background: #dcfce7;
  border-radius: 20px;
}

.live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.updated-label {
  font-size: 12px;
  color: #9ca3af;
}

.date-nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

/deep/ .timeframe-toggle {
  .btn {
    font-size: 12px;
    font-weight: 600;
  }
}

.date-label {
  min-width: 180px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

/* Phones: the label's fixed 180px minimum pushed the right chevron off the
   screen. Let the nav span the row with the arrows pinned at the edges and
   the label flexing between them, so the whole control always fits. */
@media (max-width: 575.98px) {
  .date-nav {
    width: 100%;
    justify-content: space-between;
    gap: 6px;
  }

  .date-label {
    min-width: 0;
    flex: 1 1 auto;
    font-size: 13px;
  }
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dashboard-filters-row {
  margin-top: 12px;
}

/deep/ .dashboard-filters-row {
  .custom-select {
    font-size: 13px;
    border-color: #d1d5db;
    border-radius: 8px;
    min-height: 34px;
  }
}
</style>
